import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { getMemberAppLinkQr, reissueMemberAppLinkQr } from '../../api/memberAppLink';
import CommonModalLayout from '../../form-components/CommonModalLayout';
import CommonModalButton from '../../form-components/CommonModalButton';
import CommonDeleteModal from '../../form-components/CommonDeleteModal';
import CommonLoading from '../../form-components/CommonLoading';

const QR_SIZE = 240;
const PRINT_GUIDE = '바디스위치 앱에서 스캔해 센터와 연동하세요';

const GuideText = styled(Typography)({
  fontSize: '14px',
  color: '#525252',
  lineHeight: 1.6,
});

const QrBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  margin: '24px 0 8px',
  padding: '20px',
  border: '1px solid #D9D9D9',
  borderRadius: '12px',
});

const escapeHtml = text =>
  String(text).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

/**
 * 지점 앱 연동 QR — 발급된 QR 을 보여주고 다운로드·인쇄·재발급한다.
 */
const AppLinkQrModal = ({ open, onClose, branchId }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canvasWrapRef = useRef(null);
  const [reissueOpen, setReissueOpen] = useState(false);
  const queryKey = ['memberAppLinkQr', branchId];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => getMemberAppLinkQr(branchId),
    enabled: open && !!branchId,
  });

  const reissueMutation = useMutation({
    mutationFn: () => reissueMemberAppLinkQr(branchId),
    onSuccess: result => {
      queryClient.setQueryData(queryKey, result);
      toast.success('새 QR을 발급했습니다.');
      setReissueOpen(false);
    },
  });

  const getDataUrl = () => canvasWrapRef.current?.querySelector('canvas')?.toDataURL('image/png');

  const handleDownload = () => {
    const url = getDataUrl();
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data.branchName}_앱연동QR.png`;
    link.click();
  };

  const handlePrint = () => {
    const url = getDataUrl();
    if (!url) return;
    const win = window.open('', '_blank', 'width=640,height=800');
    if (!win) {
      toast.error('팝업이 차단되어 인쇄 창을 열 수 없습니다.');
      return;
    }
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(data.branchName)} 앱 연동 QR</title>
<style>body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;text-align:center}
h1{font-size:32px;margin:0 0 24px}img{width:360px;height:360px}p{font-size:18px;margin:24px 0 0;color:#333}</style></head>
<body><h1>${escapeHtml(data.branchName)}</h1><img src="${url}" alt="QR" onload="window.focus();window.print();"/><p>${PRINT_GUIDE}</p></body></html>`);
    win.document.close();
  };

  return (
    <>
      <CommonModalLayout
        open={open}
        onClose={onClose}
        title={data ? `${data.branchName} 앱 연동 QR` : '앱 연동 QR'}
        width={520}
        footer={
          data && (
            <>
              <CommonModalButton variant="cancel" onClick={() => setReissueOpen(true)}>
                새로 발급
              </CommonModalButton>
              <CommonModalButton variant="dark" onClick={handleDownload}>
                QR 다운로드
              </CommonModalButton>
              <CommonModalButton onClick={handlePrint}>QR 인쇄</CommonModalButton>
            </>
          )
        }>
        <GuideText>
          센터에 이 QR을 비치해 주세요. 회원이 바디스위치 앱 [센터 연동하기] → [QR코드로 등록하기]에서 스캔하면 바로 연동돼요.
        </GuideText>
        <GuideText>이 센터에서 산 상품이 없는 회원은 잠재회원으로 등록돼요.</GuideText>
        {isLoading && <CommonLoading />}
        {isError && <GuideText sx={{ color: '#ef0c23', mt: 2 }}>QR을 불러오지 못했습니다.</GuideText>}
        {data && (
          <QrBox ref={canvasWrapRef}>
            <QRCodeCanvas value={data.qrText} size={QR_SIZE} level="M" includeMargin />
          </QrBox>
        )}
      </CommonModalLayout>
      <CommonDeleteModal
        open={reissueOpen}
        onClose={() => setReissueOpen(false)}
        onConfirm={() => reissueMutation.mutate()}
        title="QR을 새로 발급하시겠습니까?"
        description="새로 발급하면 지금 붙어 있는 QR은 더 이상 연동되지 않아요. 새 QR을 다시 비치해야 해요."
        confirmText="새로 발급"
        isLoading={reissueMutation.isPending}
      />
    </>
  );
};

export default AppLinkQrModal;
